import React, { useState, useEffect } from 'react';
import { roleManagementService } from '../../services/roleManagementService';
import { authService } from '../../services/authService';
import { useAbility } from '../../hooks/useAbility';
import { Loader, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import './RoleManagement.css';

const ROLES = ['Administrador', 'Ventas', 'Producción', 'Logística', 'Contabilidad', 'Integración'];

const RoleManagement = () => {
  const { updateAbility } = useAbility();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    const { user } = await authService.getCurrentUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    const { users: userList, error: err } = await roleManagementService.listUsers();
    
    if (err) {
      setError(err);
    } else {
      setUsers(userList || []);
    }
    
    setLoading(false);
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUserId && newRole !== 'Administrador') {
      setError('No puedes remover tu propio rol de Administrador');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setSaving(userId);
    setError(null);
    setSuccess(null);

    const { success: updateSuccess, error: err } = await roleManagementService.updateUserRole(userId, newRole);

    if (err) {
      setError(err);
      setTimeout(() => setError(null), 5000);
    } else if (updateSuccess) {
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      setSuccess(`Rol actualizado exitosamente para ${users.find(u => u.id === userId)?.email}`);
      setTimeout(() => setSuccess(null), 3000);
      
      // If updating current user's role, update ability context
      if (userId === currentUserId) {
        updateAbility(newRole);
        // Reload page after 1 second to refresh all UI with new permissions
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }

    setSaving(null);
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Administrador': return '#2c5f2d';
      case 'Ventas': return '#0066cc';
      case 'Producción': return '#ff6b35';
      case 'Logística': return '#8b5cf6';
      case 'Contabilidad': return '#059669';
      case 'Integración': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="role-management-loading">
        <Loader size={40} className="spinner" />
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="role-management">
      <div className="role-management-header">
        <div className="header-title">
          <Shield size={32} />
          <div>
            <h1>Gestión de Roles</h1>
            <p>Administra los roles y permisos de los usuarios del sistema</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      <div className="role-management-controls">
        <input
          type="text"
          placeholder="Buscar por email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button onClick={loadUsers} className="btn-refresh">
          Actualizar Lista
        </button>
      </div>

      <div className="role-management-info">
        <h3>Descripción de Roles</h3>
        <div className="roles-grid">
          <div className="role-card">
            <div className="role-badge" style={{ backgroundColor: getRoleBadgeColor('Administrador') }}>
              Administrador
            </div>
            <p>Acceso completo al sistema. Puede gestionar todos los módulos y usuarios.</p>
          </div>
          <div className="role-card">
            <div className="role-badge" style={{ backgroundColor: getRoleBadgeColor('Ventas') }}>
              Ventas
            </div>
            <p>Gestión de clientes, pedidos y visualización de inventario.</p>
          </div>
          <div className="role-card">
            <div className="role-badge" style={{ backgroundColor: getRoleBadgeColor('Producción') }}>
              Producción
            </div>
            <p>Gestión de productos, inventario, plan de cosecha y archivos.</p>
          </div>
          <div className="role-card">
            <div className="role-badge" style={{ backgroundColor: getRoleBadgeColor('Logística') }}>
              Logística
            </div>
            <p>Gestión completa de exportaciones, preparativos y embarques.</p>
          </div>
          <div className="role-card">
            <div className="role-badge" style={{ backgroundColor: getRoleBadgeColor('Contabilidad') }}>
              Contabilidad
            </div>
            <p>Acceso de solo lectura a todos los módulos principales.</p>
          </div>
          <div className="role-card">
            <div className="role-badge" style={{ backgroundColor: getRoleBadgeColor('Integración') }}>
              Integración
            </div>
            <p>Acceso de solo lectura exclusivamente al inventario.</p>
          </div>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Rol Actual</th>
              <th>Cambiar Rol</th>
              <th>Fecha Creación</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-users">
                  No se encontraron usuarios
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className={user.id === currentUserId ? 'current-user' : ''}>
                  <td>
                    <div className="user-email">
                      {user.email}
                      {user.id === currentUserId && (
                        <span className="current-badge">Tú</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span 
                      className="role-badge-small" 
                      style={{ backgroundColor: getRoleBadgeColor(user.role) }}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <div className="role-select-container">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={saving === user.id}
                        className="role-select"
                      >
                        {ROLES.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      {saving === user.id && (
                        <Loader size={16} className="saving-spinner" />
                      )}
                    </div>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString('es-CL')}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoleManagement;
